import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { adminApi } from '../api'
import {
  PlaceNameAudit,
  PlaceNameAuditKind,
  PlaceNameAuditStatus,
} from '../api/placeNameAudit'
import { toastUtil } from '../../utils/toast'
import AdminSidebar from '../components/AdminSidebar'

/**
 * 갈래마다 사람이 할 일이 다르다. `act` 는 **무엇을 판단해야 하나**다.
 *
 * 'changed' 를 맨 위에 두는 이유는 그것만 진짜 판단이 필요하기 때문이다.
 */
const KIND: Record<
  PlaceNameAuditKind,
  { label: string; color: string; why: string; act: string }
> = {
  changed: {
    label: '달라짐',
    color: '#B8860B',
    why: '앞부분부터 다릅니다. 가게가 상호를 바꿨거나, 표기를 바꿨거나, 카카오가 고쳐 적었습니다.',
    act: '주소가 같으면 같은 가게입니다. 카카오 링크를 열어 어느 이름이 맞는지 보고 고르세요.',
  },
  shorter: {
    label: '우리가 더 긺',
    color: '#918063',
    why: '카카오가 지점 표기 같은 것을 뺐습니다.',
    act: '대개 우리 이름이 더 구체적입니다. 그냥 두는 쪽이 낫습니다.',
  },
  branch: {
    label: '지점명 붙음',
    color: '#567423',
    why: '뒤에 지점명이 붙었습니다. 글자는 그대로 두고 더 구체적이 된 것입니다.',
    act: '받아도 안전합니다. 같은 상호의 다른 지점과 구별돼서 오히려 낫습니다.',
  },
  spacing: {
    label: '띄어쓰기만',
    color: '#567423',
    why: '글자가 한 자도 다르지 않습니다. 띄어쓰기만 다릅니다.',
    act: '받아도 안전합니다. 카카오 띄어쓰기를 따르면 검색 대조도 잘 맞습니다.',
  },
}

const ORDER: PlaceNameAuditKind[] = ['changed', 'shorter', 'branch', 'spacing']

const AdminPlaceNames: React.FC = () => {
  const navigate = useNavigate()
  const [audits, setAudits] = useState<PlaceNameAudit[]>([])
  const [counts, setCounts] = useState<Partial<Record<PlaceNameAuditKind, number>>>({})
  const [status, setStatus] = useState<PlaceNameAuditStatus | 'all'>('pending')
  const [isLoading, setIsLoading] = useState(true)
  const [busy, setBusy] = useState<number | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await adminApi.getPlaceNameAudits({ status })
      setAudits(res.audits)
      setCounts(res.counts ?? {})
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login')
        return
      }
      toastUtil.error('목록을 불러오지 못했습니다')
    } finally {
      setIsLoading(false)
    }
  }, [navigate, status])

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login')
      return
    }
    load()
  }, [navigate, load])

  const decide = async (placeId: number, decision: 'apply' | 'keep') => {
    setBusy(placeId)
    try {
      await adminApi.decidePlaceNameAudit(placeId, decision)
      toastUtil.success(decision === 'apply' ? '이름을 바꿨습니다' : '우리 이름을 유지합니다')
      setAudits((prev) => prev.filter((a) => a.place_id !== placeId))
    } catch (error: any) {
      toastUtil.error(error.response?.data?.message ?? '처리 중 오류가 발생했습니다')
    } finally {
      setBusy(null)
    }
  }

  const grouped = useMemo(
    () =>
      ORDER.map((kind) => ({
        kind,
        rows: audits.filter((a) => a.kind === kind),
      })).filter((g) => g.rows.length > 0),
    [audits]
  )

  const total = ORDER.reduce((sum, k) => sum + (counts[k] ?? 0), 0)

  return (
    <div className="min-h-screen bg-backgroundColorBg2 flex">
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-brandPrimary-10% sticky top-0 z-10">
          <div className="px-24px py-16px flex items-center justify-end">
            <button
              onClick={() => {
                localStorage.removeItem('admin_token')
                navigate('/admin/login')
              }}
              className="text-style-button2 text-textColorText2 hover:text-brown-80%-main transition-colors"
            >
              로그아웃
            </button>
          </div>
        </header>

        <main className="flex-1 px-24px py-32px flex flex-col gap-24px max-w-[1100px]">
          <section className="bg-white rounded-16px shadow-sm p-32px">
            <h2 className="text-style-title2 text-textColorText2">
              이름이 어긋난 장소
            </h2>
            <p className="mt-8px text-style-body2 text-textColorText3">
              우리가 들고 있는 상호와 카카오의 지금 이름이 다른 곳입니다.
              이름이 어긋나면 <strong>같은 가게가 검색에 두 번 뜨고, 내 기록도
              없는 것처럼 보입니다.</strong>
            </p>
            <p className="mt-4px text-style-captionL text-textColorText3">
              목록은 <code>php artisan app:audit-place-names</code> 가 훑어서 적어
              둡니다 (5,600곳에 40분). 이 화면은 그 결과만 읽습니다.
            </p>

            <div className="mt-20px flex flex-wrap items-center gap-12px">
              {ORDER.map((k) =>
                counts[k] ? (
                  <span
                    key={k}
                    className="inline-flex items-center gap-6px px-12px py-6px rounded-full text-12px font-medium"
                    style={{ color: KIND[k].color, backgroundColor: `${KIND[k].color}1A` }}
                  >
                    {KIND[k].label} {counts[k]}
                  </span>
                ) : null
              )}
              {total === 0 && !isLoading && (
                <span className="text-style-body2 text-textColorText3">
                  아직 결정할 것이 없습니다.
                </span>
              )}

              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
                className="ml-auto py-8px px-12px border border-brandPrimary-20% rounded-8px text-style-body2 text-textColorText2 bg-transparent"
              >
                <option value="pending">결정 안 한 것</option>
                <option value="applied">바꾼 것</option>
                <option value="kept">그대로 둔 것</option>
                <option value="all">전부</option>
              </select>
            </div>
          </section>

          {isLoading && (
            <p className="text-style-body2 text-textColorText3">불러오는 중...</p>
          )}

          {grouped.map(({ kind, rows }) => (
            <section key={kind} className="bg-white rounded-16px shadow-sm p-32px">
              <div className="flex items-center gap-10px">
                <span
                  className="inline-flex items-center px-10px py-4px rounded-full text-12px font-medium"
                  style={{ color: KIND[kind].color, backgroundColor: `${KIND[kind].color}1A` }}
                >
                  {KIND[kind].label}
                </span>
                <span className="text-style-labelL text-textColorText2">
                  {rows.length}곳
                </span>
              </div>
              <p className="mt-8px text-style-captionL text-textColorText3">
                {KIND[kind].why}
              </p>
              <p className="mt-2px text-style-captionL text-textColorText2">
                <b>할 일</b> — {KIND[kind].act}
              </p>

              <div className="mt-20px flex flex-col gap-10px">
                {rows.map((a) => (
                  <Row
                    key={a.place_id}
                    audit={a}
                    busy={busy === a.place_id}
                    onDecide={decide}
                    readOnly={a.status !== 'pending'}
                  />
                ))}
              </div>
            </section>
          ))}
        </main>
      </div>
    </div>
  )
}

const Row: React.FC<{
  audit: PlaceNameAudit
  busy: boolean
  readOnly: boolean
  onDecide: (placeId: number, decision: 'apply' | 'keep') => void
}> = ({ audit, busy, readOnly, onDecide }) => (
  <div className="border border-brandPrimary-10% rounded-8px p-16px">
    <div className="flex flex-wrap items-baseline gap-x-10px gap-y-4px">
      <span className="text-style-body1 text-textColorText2 line-through decoration-textColorText3">
        {audit.was}
      </span>
      <span className="text-textColorText3">→</span>
      <span className="text-style-labelL text-brown-80%-main">{audit.now}</span>

      {audit.review_count > 0 && (
        <span className="text-style-captionL" style={{ color: '#B8860B' }}>
          기록 {audit.review_count}건
        </span>
      )}
      {audit.save_count > 0 && (
        <span className="text-style-captionL text-textColorText3">
          저장 {audit.save_count}건
        </span>
      )}
    </div>

    <p className="mt-6px text-style-captionL text-textColorText3">
      {audit.address ?? '주소 없음'}
      {audit.kakao_category ? ` · ${audit.kakao_category}` : ''}
    </p>

    {audit.current_name !== audit.was && (
      <p className="mt-4px text-style-captionL" style={{ color: '#B8860B' }}>
        훑은 뒤에 바뀌었습니다 — 지금 DB 이름은 “{audit.current_name}”
      </p>
    )}

    <div className="mt-12px flex flex-wrap items-center gap-10px">
      <a
        href={audit.kakao_url}
        target="_blank"
        rel="noreferrer"
        className="text-style-captionL text-brown-80%-main hover:underline"
      >
        카카오에서 보기 ↗
      </a>

      {readOnly ? (
        <span className="ml-auto text-style-captionL text-textColorText3">
          {audit.status === 'applied' ? '바꿈' : '그대로 둠'}
        </span>
      ) : (
        <div className="ml-auto flex items-center gap-8px">
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide(audit.place_id, 'keep')}
            className="py-8px px-14px rounded-8px text-style-button2 text-textColorText2 border border-brandPrimary-20% hover:bg-backgroundColorBg2 transition-colors disabled:opacity-50"
          >
            우리 이름 유지
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide(audit.place_id, 'apply')}
            className="py-8px px-14px rounded-8px text-style-button2 text-white bg-4B3621-80% hover:bg-brandPrimary-70% transition-colors disabled:bg-4B3621-20%"
          >
            {busy ? '...' : '카카오 이름으로'}
          </button>
        </div>
      )}
    </div>
  </div>
)

export default AdminPlaceNames
